import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminSidebar from '../../../components/AdminSidebar';
import { getAdminToken } from '../../../lib/adminAuth';

const NAVY = '#0a2540'; const ORANGE = '#F37428'; const BLUE = '#0176CE';
const API_BASE = import.meta.env.VITE_API_URL || 'https://masterfileai-api.azurewebsites.net';

function apiFetch(path, opts) {
  const token = getAdminToken() || '';
  const sep = path.includes('?') ? '&' : '?';
  return fetch(`${API_BASE}${path}${sep}token=${token}`, {
    method: opts?.method || 'GET',
    headers: { 'Content-Type': 'text/plain' },
    body: opts?.body !== undefined ? JSON.stringify(opts.body) : undefined,
  }).then(r => r.json());
}

// Default titles to search for in Apollo
const DEFAULT_TITLES = [
  'Accounts Payable Manager',
  'Accounts Payable Director',
  'AP Manager',
  'Controller',
  'Assistant Controller',
  'CFO',
  'Chief Financial Officer',
  'Director of Finance',
  'VP Finance',
  'Shared Services Manager',
  'Shared Services Director',
  'Finance Manager',
];

const DEFAULT_EXCLUSIONS = [
  'Supervisor', 'Lead', 'Clerk', 'Specialist', 'Coordinator',
  'Technician', 'Analyst', 'Retired', 'Former', 'Intern',
  'Temp', 'Contract', 'Volunteer', 'Freelance', 'Student', 'Member', 'Staff',
];

const INDUSTRIES = [
  'Manufacturing', 'Healthcare', 'Retail', 'Food and Beverages',
  'Automotive', 'Construction', 'Wholesale', 'Distribution',
  'Chemical', 'Technology', 'Professional Services', 'Transportation',
  'Real Estate', 'Government', 'Education',
];

const EMPLOYEE_RANGES = [
  { label: '11–50',     value: '11,50'           },
  { label: '51–200',    value: '51,200'           },
  { label: '201–500',   value: '201,500'          },
  { label: '501–1,000', value: '501,1000'         },
  { label: '1,001–5,000', value: '1001,5000'      },
  { label: '5,000+',    value: '5001,1000000000'  },
];

function TagInput({ tags, onChange, placeholder }) {
  const [input, setInput] = useState('');

  function add(val) {
    const v = val.trim();
    if (v && !tags.includes(v)) onChange([...tags, v]);
    setInput('');
  }

  function remove(t) { onChange(tags.filter(x => x !== t)); }

  return (
    <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 10px', background: '#fff', minHeight: 44, display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
      {tags.map(t => (
        <span key={t} style={{ background: 'rgba(243,116,40,0.10)', color: ORANGE, borderRadius: 20, padding: '3px 10px', fontSize: 12, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 5 }}>
          {t}
          <span onClick={() => remove(t)} style={{ cursor: 'pointer', opacity: 0.6, fontWeight: 700 }}>×</span>
        </span>
      ))}
      <input
        value={input}
        onChange={e => setInput(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); add(input); } }}
        onBlur={() => input.trim() && add(input)}
        placeholder={tags.length === 0 ? placeholder : ''}
        style={{ border: 'none', outline: 'none', fontSize: 13, flex: '1 1 120px', minWidth: 0, background: 'transparent' }}
      />
    </div>
  );
}

export default function ApolloPull() {
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [titles, setTitles] = useState(DEFAULT_TITLES);
  const [exclusions, setExclusions] = useState(DEFAULT_EXCLUSIONS);
  const [industries, setIndustries] = useState([]);
  const [empRanges, setEmpRanges] = useState(['51,200', '201,500', '501,1000']);
  const [count, setCount] = useState(50);
  const [emailStatus, setEmailStatus] = useState(['verified']);
  const [countries, setCountries]     = useState(['United States']);
  const [pulling, setPulling] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    apiFetch('/api/console/b2b/apollo-pull/config')
      .then(r => setConfig(r))
      .catch(() => setConfig({ configured: false, standard_titles: [] }));
  }, []);

  function toggleRange(v) {
    setEmpRanges(r => r.includes(v) ? r.filter(x => x !== v) : [...r, v]);
  }

  function toggleIndustry(v) {
    setIndustries(r => r.includes(v) ? r.filter(x => x !== v) : [...r, v]);
  }

  async function handlePull() {
    setPulling(true);
    setError('');
    setResult(null);
    try {
      const res = await apiFetch('/api/console/b2b/apollo-pull', {
        method: 'POST',
        body: {
          titles,
          exclusions,
          industries,
          employee_ranges: empRanges,
          countries,
          count,
          email_statuses: emailStatus,
        },
      });
      if (res.error) throw new Error(res.error);
      setResult(res);
    } catch (e) {
      setError(e.message);
    }
    setPulling(false);
  }

  const noKey = config && !config.configured;

  return (
    <div style={s.page}>
      <AdminSidebar active="b2b-apollo-pull" />
      <main style={s.main}>

        <div style={s.header}>
          <div>
            <h1 style={s.title}>Apollo Pull</h1>
            <p style={s.subtitle}>Define your search criteria and pull contacts directly from Apollo into your CRM. New contacts are auto-classified by title.</p>
          </div>
          <button style={s.linkBtn} onClick={() => navigate('/admin/b2b/contacts')}>View Contacts →</button>
        </div>

        {noKey && (
          <div style={s.warnBox}>
            <strong>⚠ APOLLO_API_KEY not configured.</strong> Add it in Azure Portal → masterfileai-api → Configuration → Application settings.
          </div>
        )}

        <div style={s.twoCol}>
          {/* ── Left: Criteria ── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

            <div style={s.card}>
              <div style={s.cardTitle}>Job Titles</div>
              <p style={s.hint}>Press Enter or comma to add. These are the titles Apollo will search for.</p>
              <TagInput tags={titles} onChange={setTitles} placeholder="Type a title and press Enter…" />
              <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                {['Accounts Payable Manager','Controller','CFO','Shared Services Manager'].filter(t => !titles.includes(t)).map(t => (
                  <button key={t} style={s.addTag} onClick={() => setTitles(ts => [...ts, t])}>+ {t}</button>
                ))}
              </div>
            </div>

            <div style={{ ...s.card, borderTop: '3px solid #ef4444' }}>
              <div style={{ ...s.cardTitle, color: '#dc2626' }}>Excluded Titles</div>
              <p style={s.hint}>Apollo will filter out anyone whose title contains these words. Add or remove as needed.</p>
              <div style={{ border: '1px solid #fecaca', borderRadius: 8, padding: '8px 10px', background: '#fff', minHeight: 44, display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', marginTop: 8 }}>
                {exclusions.map(t => (
                  <span key={t} style={{ background: 'rgba(239,68,68,0.08)', color: '#dc2626', borderRadius: 20, padding: '3px 10px', fontSize: 12, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 5 }}>
                    {t}
                    <span onClick={() => setExclusions(ex => ex.filter(x => x !== t))} style={{ cursor: 'pointer', opacity: 0.6, fontWeight: 700 }}>×</span>
                  </span>
                ))}
                <input
                  placeholder={exclusions.length === 0 ? 'Type a word to exclude and press Enter…' : ''}
                  style={{ border: 'none', outline: 'none', fontSize: 13, flex: '1 1 120px', minWidth: 0, background: 'transparent' }}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ',') {
                      e.preventDefault();
                      const v = e.target.value.trim();
                      if (v && !exclusions.includes(v)) setExclusions(ex => [...ex, v]);
                      e.target.value = '';
                    }
                  }}
                />
              </div>
            </div>

            <div style={s.card}>
              <div style={s.cardTitle}>Company Size</div>
              <p style={s.hint}>Leave all unchecked to search all sizes.</p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
                {EMPLOYEE_RANGES.map(r => (
                  <label key={r.value} style={{ ...s.checkLabel, background: empRanges.includes(r.value) ? 'rgba(243,116,40,0.10)' : '#f8fafc', border: `1px solid ${empRanges.includes(r.value) ? ORANGE : '#e2e8f0'}`, color: empRanges.includes(r.value) ? ORANGE : '#374151' }}>
                    <input type="checkbox" checked={empRanges.includes(r.value)} onChange={() => toggleRange(r.value)} style={{ display: 'none' }} />
                    {r.label}
                  </label>
                ))}
              </div>
            </div>

            <div style={s.card}>
              <div style={s.cardTitle}>Industries</div>
              <p style={s.hint}>Leave all unchecked to search all industries.</p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
                {INDUSTRIES.map(ind => (
                  <label key={ind} style={{ ...s.checkLabel, background: industries.includes(ind) ? 'rgba(1,118,206,0.08)' : '#f8fafc', border: `1px solid ${industries.includes(ind) ? BLUE : '#e2e8f0'}`, color: industries.includes(ind) ? BLUE : '#374151' }}>
                    <input type="checkbox" checked={industries.includes(ind)} onChange={() => toggleIndustry(ind)} style={{ display: 'none' }} />
                    {ind}
                  </label>
                ))}
              </div>
            </div>

            <div style={s.card}>
              <div style={s.cardTitle}>Country</div>
              <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 8 }}>Leave all unchecked to search all countries.</div>
              <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                {['United States', 'Canada', 'Mexico'].map(c => (
                  <label key={c} style={{ ...s.checkLabel, background: countries.includes(c) ? 'rgba(243,116,40,0.10)' : '#f8fafc', border: `1px solid ${countries.includes(c) ? ORANGE : '#e2e8f0'}`, color: countries.includes(c) ? ORANGE : '#374151' }}>
                    <input type="checkbox" checked={countries.includes(c)} onChange={e => setCountries(prev => e.target.checked ? [...prev, c] : prev.filter(x => x !== c))} style={{ display: 'none' }} />
                    {c}
                  </label>
                ))}
              </div>
            </div>

            <div style={s.card}>
              <div style={s.cardTitle}>Email Status</div>
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                {['verified', 'likely to engage', 'guessed'].map(st => (
                  <label key={st} style={{ ...s.checkLabel, background: emailStatus.includes(st) ? 'rgba(16,185,129,0.10)' : '#f8fafc', border: `1px solid ${emailStatus.includes(st) ? '#10b981' : '#e2e8f0'}`, color: emailStatus.includes(st) ? '#059669' : '#374151' }}>
                    <input type="checkbox" checked={emailStatus.includes(st)} onChange={e => setEmailStatus(prev => e.target.checked ? [...prev, st] : prev.filter(x => x !== st))} style={{ display: 'none' }} />
                    {st}
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* ── Right: Pull controls + results ── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

            <div style={s.card}>
              <div style={s.cardTitle}>Record Count</div>
              <p style={s.hint}>How many contacts to pull (1–200). Apollo deduplication happens server-side.</p>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginTop: 12 }}>
                <input
                  type="range" min={10} max={200} step={10}
                  value={count} onChange={e => setCount(+e.target.value)}
                  style={{ flex: 1, accentColor: ORANGE }}
                />
                <div style={{ minWidth: 50, textAlign: 'right', fontSize: 24, fontWeight: 700, color: NAVY }}>{count}</div>
              </div>
              <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
                {[25, 50, 100, 200].map(n => (
                  <button key={n} style={{ ...s.addTag, ...(count === n ? { background: ORANGE, color: '#fff', borderColor: ORANGE } : {}) }} onClick={() => setCount(n)}>{n}</button>
                ))}
              </div>
            </div>

            <div style={s.card}>
              <div style={s.cardTitle}>What happens during pull</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 10 }}>
                {[
                  ['🔍', 'Search Apollo with your criteria'],
                  ['🔄', 'Skip contacts your team already owns in Apollo'],
                  ['🏢', 'Create account records for new companies'],
                  ['👤', 'Import contact with title + email'],
                  ['🏷', 'Auto-classify title using AI (cached)'],
                ].map(([icon, text]) => (
                  <div key={text} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 13, color: '#374151' }}>
                    <span style={{ fontSize: 16, flexShrink: 0 }}>{icon}</span>
                    <span>{text}</span>
                  </div>
                ))}
              </div>
            </div>

            {error && <div style={s.errorBox}>{error}</div>}

            <button
              style={{ ...s.pullBtn, opacity: (noKey || pulling || titles.length === 0) ? 0.5 : 1 }}
              disabled={noKey || pulling || titles.length === 0}
              onClick={handlePull}
            >
              {pulling ? (
                <span>⟳ Pulling from Apollo… (this may take 30–60s)</span>
              ) : (
                <span>🚀 Pull {count} Contacts from Apollo</span>
              )}
            </button>

            {/* Results */}
            {result && (
              <div style={s.resultCard}>
                <div style={{ fontSize: 15, fontWeight: 700, color: NAVY, marginBottom: 12 }}>✅ Pull Complete — {result.total} returned by Apollo</div>
                <div style={s.statRow}>
                  <Stat label="Imported" val={result.imported} color="#059669" />
                  <Stat label="Skipped (dupes)" val={result.skipped} color="#94a3b8" />
                  <Stat label="New Companies" val={result.accounts_created} color={BLUE} />
                  <Stat label="Titles Classified" val={result.classified} color="#6c63ff" />
                </div>

                {/* Raw Apollo response — what did Apollo actually send? */}
                {result.apollo_returned?.length > 0 && (
                  <div style={{ marginTop: 16 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
                      What Apollo returned ({result.apollo_returned.length} people)
                    </div>
                    <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr 1fr 70px 60px', background: '#f8fafc', padding: '6px 12px', fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                        <span>Name / Title</span><span>Company</span><span>Location</span><span>Email?</span><span>Owned?</span>
                      </div>
                      {result.apollo_returned.map((p, i) => (
                        <div key={i} style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr 1fr 70px 60px', padding: '7px 12px', fontSize: 11, borderTop: '1px solid #f1f5f9', background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                          <div>
                            <div style={{ fontWeight: 600, color: NAVY }}>{p.name || '—'}</div>
                            <div style={{ color: '#94a3b8', fontSize: 10 }}>{p.title}</div>
                          </div>
                          <div style={{ color: '#374151', alignSelf: 'center', fontSize: 11 }}>{p.company || '—'}</div>
                          <div style={{ color: '#64748b', alignSelf: 'center', fontSize: 11 }}>{p.location || '—'}</div>
                          <div style={{ alignSelf: 'center', textAlign: 'center' }}>
                            {p.has_email
                              ? <span style={{ fontSize: 9, fontWeight: 700, color: '#059669', background: '#f0fdf4', padding: '2px 6px', borderRadius: 10 }}>✓</span>
                              : <span style={{ fontSize: 9, fontWeight: 700, color: '#dc2626', background: '#fef2f2', padding: '2px 6px', borderRadius: 10 }}>✗</span>}
                          </div>
                          <div style={{ alignSelf: 'center', textAlign: 'center' }}>
                            {p.revealed === true
                              ? <span style={{ fontSize: 9, fontWeight: 700, color: '#94a3b8', background: '#f1f5f9', padding: '2px 6px', borderRadius: 10 }}>yes</span>
                              : p.revealed === false
                              ? <span style={{ fontSize: 9, fontWeight: 700, color: '#059669', background: '#f0fdf4', padding: '2px 6px', borderRadius: 10 }}>no</span>
                              : <span style={{ fontSize: 9, color: '#94a3b8' }}>—</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
                  <button style={s.primaryBtn} onClick={() => navigate('/admin/b2b/contacts')}>View Contacts →</button>
                  <button style={s.secondaryBtn} onClick={() => { setResult(null); setError(''); }}>Pull Again</button>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

function Stat({ label, val, color }) {
  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontSize: 26, fontWeight: 700, color: color || '#0a2540', lineHeight: 1 }}>{val}</div>
      <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
    </div>
  );
}

const s = {
  page:       { display: 'flex', minHeight: '100vh', fontFamily: "'Inter', system-ui, sans-serif" },
  main:       { flex: 1, background: '#f0f5fb', padding: '36px 40px', overflowY: 'auto' },
  header:     { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 28 },
  title:      { color: NAVY, fontSize: 24, fontWeight: 700, margin: '0 0 6px' },
  subtitle:   { color: '#64748b', fontSize: 13, margin: 0, lineHeight: 1.6, maxWidth: 560 },
  linkBtn:    { background: 'transparent', border: '1px solid #e2e8f0', borderRadius: 8, color: BLUE, cursor: 'pointer', fontSize: 13, padding: '8px 16px', flexShrink: 0 },
  warnBox:    { background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 10, color: '#92400e', fontSize: 13, padding: '14px 18px', marginBottom: 20 },
  twoCol:     { display: 'grid', gridTemplateColumns: '1fr 380px', gap: 24, alignItems: 'start' },
  card:       { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '20px 22px' },
  cardTitle:  { fontSize: 13, fontWeight: 700, color: NAVY, marginBottom: 2 },
  hint:       { fontSize: 12, color: '#94a3b8', margin: '4px 0 0', lineHeight: 1.5 },
  checkLabel: { display: 'inline-flex', alignItems: 'center', borderRadius: 20, padding: '5px 12px', fontSize: 12, fontWeight: 500, cursor: 'pointer', userSelect: 'none', transition: 'all 0.12s' },
  addTag:     { background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 20, color: '#64748b', cursor: 'pointer', fontSize: 11, fontWeight: 500, padding: '3px 10px' },
  pullBtn:    { background: ORANGE, color: '#fff', border: 'none', borderRadius: 10, padding: '14px 20px', fontSize: 15, fontWeight: 700, cursor: 'pointer', width: '100%', textAlign: 'center' },
  primaryBtn: { background: ORANGE, color: '#fff', border: 'none', borderRadius: 8, padding: '9px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  secondaryBtn: { background: '#f1f5f9', color: '#374151', border: 'none', borderRadius: 8, padding: '9px 16px', fontSize: 13, cursor: 'pointer' },
  errorBox:   { background: 'rgba(239,68,68,0.07)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 8, color: '#dc2626', fontSize: 13, padding: '12px 16px' },
  resultCard: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '24px' },
  statRow:    { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, background: '#f8fafc', borderRadius: 10, padding: '16px' },
};
